import React, { useState, useEffect, useCallback } from 'react'; // Adicionar useCallback para melhor performance
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Camera } from 'lucide-react';
import api from '../services/api';
import { authService } from '../services/authService';
import { toast } from '../hooks/use-toast';

// Defina a interface User se for necessário para evitar 'any' no retorno de getCurrentUser
// interface User {
//   id: string;
//   nome: string;
//   email: string;
//   perfil: string;
//   avatarUrl?: string | null;
// }

interface PerfilModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PerfilModal({ isOpen, onOpenChange }: PerfilModalProps) {
  // ATENÇÃO: Se o user não for reativo (ex: não usa um hook global de estado),
  // as informações dele só serão carregadas na primeira montagem.
  // Para fins de demonstração, vamos carregar ele aqui, mas considere usar um Context ou Redux/Zustand.
  const initialUser = authService.getCurrentUser(); // Pega o usuário na inicialização

  const [user, setUser] = useState(initialUser); // Armazena o usuário no estado para reatividade se necessário

  const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5001')?.replace(/\/api$/, '');
  const [nome, setNome] = useState(user?.nome || '');
  // avatarPreview será a URL do objeto ou a base64
  const [avatarPreview, setAvatarPreview] = useState<string | null>(user?.avatarUrl ? `${API_BASE_URL}${user.avatarUrl.startsWith('/') ? '' : '/'}${user.avatarUrl}` : null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [senhaAtual, setSenhaAtual] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmarNovaSenha, setConfirmarNovaSenha] = useState('');
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);


  // 1. Corrigir o reset do estado e a inicialização.
  // Se o `user` mudar (o que pode acontecer se for carregado assincronamente),
  // o estado interno do modal precisa ser redefinido.
  useEffect(() => {
    if (isOpen && user) {
      setNome(user.nome);
      // Garante que o preview inicial é o avatarUrl do usuário com a URL completa
      const fullAvatarUrl = user.avatarUrl
        ? `${API_BASE_URL}${user.avatarUrl.startsWith('/') ? '' : '/'}${user.avatarUrl}`
        : null;
      setAvatarPreview(fullAvatarUrl);
      setAvatarFile(null);
      setSenhaAtual('');
      setNovaSenha('');
      setConfirmarNovaSenha('');
      setError(null);
      setSuccessMessage(null);
    }
    // Não é necessário um else if (!isOpen) a menos que você queira limpar 
    // o estado interno (como erros e senhas) ao fechar, mesmo que o usuário
    // decida abrir novamente depois.
  }, [isOpen, user]);

  const handleAvatarChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setError(null);
    setSuccessMessage(null);

    if (!file) return;

    // Validate file type
    const validImageTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!validImageTypes.includes(file.type)) {
      setError('Formato de arquivo não suportado. Use JPG, PNG ou WebP.');
      event.target.value = ''; // Reset input
      return;
    }

    // Validate file size (max 2MB)
    const maxSize = 2 * 1024 * 1024; // 2MB
    if (file.size > maxSize) {
      setError('A imagem não pode ser maior que 2MB.');
      event.target.value = ''; // Reset input
      return;
    }

    // Create preview and set the file
    const reader = new FileReader();
    reader.onload = (e) => {
      setAvatarPreview(e.target?.result as string);
      setAvatarFile(file);
    };
    reader.readAsDataURL(file);

    // Optional: Validate dimensions if needed
    const img = new Image();
    img.onload = () => {
      // if (img.width > 1024 || img.height > 1024) {
      //   setError('A imagem não pode ser maior que 1024x1024 pixels.');
      //   event.target.value = '';
      //   return;
      // }

      // If all validations pass, set the file and preview
      setAvatarFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target?.result && typeof e.target.result === 'string') {
          setAvatarPreview(e.target.result);
        }
      };
      reader.onerror = () => {
        setError('Erro ao processar a imagem. Tente novamente.');
        setAvatarFile(null);
        setAvatarPreview(user?.avatarUrl || null);
      };
      reader.readAsDataURL(file);
    };

    img.onerror = () => {
      setError('A imagem selecionada é inválida.');
      event.target.value = '';
    };

    img.src = URL.createObjectURL(file);
  };

  const handleSaveProfile = async () => {
    if (!user) return;

    // Validação do nome
    if (!nome.trim()) {
      setError('O nome não pode ser vazio.');
      return;
    }

    // Validação do tamanho do arquivo (segurança extra)
    if (avatarFile && avatarFile.size > 2 * 1024 * 1024) {
      setError('A imagem não pode ser maior que 2MB.');
      return;
    }

    setIsSavingProfile(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const formData = new FormData();
      // Verificação para evitar salvar se o nome for vazio
      if (!nome.trim()) {
        setError('O nome não pode ser vazio.');
        setIsSavingProfile(false);
        return;
      }
      formData.append('nome', nome);

      if (avatarFile) {
        formData.append('file', avatarFile);
      }

      const response = await api.put('/usuarios/profile', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (!response.data || !response.data.usuario) {
        throw new Error('Resposta inválida do servidor');
      }

      // Atualizar usuário no estado local e no localStorage
      const updatedUser = {
        ...user,
        nome: response.data.usuario.nome || user.nome,
        avatarUrl: response.data.usuario.avatarUrl || user.avatarUrl
      };

      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));

      // Mostrar mensagem de sucesso
      toast({
        title: 'Sucesso',
        description: 'Perfil atualizado com sucesso!',
      });
      setSuccessMessage('Perfil atualizado com sucesso!');

      // Fechar o modal após 1.5 segundos
      setTimeout(() => {
        onOpenChange(false);
        // Aqui você pode adicionar um callback para notificar o componente pai
        // ou usar um contexto global para atualizar o avatar no cabeçalho
      }, 1500);
    } catch (err: any) {
      // ... (Resto da sua lógica de erro)
      console.error("Erro ao salvar perfil:", err);
      const errorMessage = err?.response?.data?.message || 'Erro ao salvar perfil. Tente novamente.';
      setError(errorMessage);
      toast({
        title: 'Erro',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleChangePassword = async () => {
    if (!user) {
      setError('Usuário não encontrado.');
      return;
    }

    // Validação das senhas
    if (!senhaAtual || !novaSenha || !confirmarNovaSenha) {
      const errorMsg = 'Preencha todos os campos de senha.';
      setError(errorMsg);
      toast({
        title: 'Erro',
        description: errorMsg,
        variant: 'destructive',
      });
      return;
    }

    if (novaSenha !== confirmarNovaSenha) {
      const errorMsg = 'As novas senhas não coincidem.';
      setError(errorMsg);
      toast({
        title: 'Erro',
        description: errorMsg,
        variant: 'destructive',
      });
      return;
    }

    setIsChangingPassword(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const response = await api.put('/usuarios/change-password', {
        senhaAtual: senhaAtual.trim(),
        novaSenha: novaSenha.trim(),
      });

      if (response.status === 200) {
        const successMsg = 'Senha alterada com sucesso!';
        setSuccessMessage(successMsg);
        toast({
          title: 'Sucesso',
          description: successMsg,
        });

        // Limpar campos de senha após sucesso
        setSenhaAtual('');
        setNovaSenha('');
        setConfirmarNovaSenha('');
      }
    } catch (err: any) {
      console.error('Erro ao alterar senha:', err);

      // Melhor tratamento de erros
      let errorMsg = 'Erro ao alterar senha. Tente novamente.';

      if (err.response) {
        // O servidor respondeu com um status de erro
        if (err.response.data && typeof err.response.data === 'object') {
          errorMsg = err.response.data.message || JSON.stringify(err.response.data);
        } else if (typeof err.response.data === 'string') {
          errorMsg = err.response.data;
        }
      } else if (err.request) {
        // A requisição foi feita mas não houve resposta
        errorMsg = 'Sem resposta do servidor. Verifique sua conexão.';
      } else {
        // Erro ao configurar a requisição
        errorMsg = err.message || 'Erro ao processar a requisição.';
      }

      setError(errorMsg);
      toast({
        title: 'Erro',
        description: errorMsg,
        variant: 'destructive',
      });
    } finally {
      setIsChangingPassword(false);
    }
  };

  const getInitials = useCallback((name: string | null | undefined) => {
    return (name ?? '')
      .split(' ')
      .map(n => n[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();
  }, []);

  if (!user) return null;

  return (
    // Usa a prop open e onOpenChange do Dialog
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] dark:bg-gray-800 dark:text-white">
        <DialogHeader>
          <DialogTitle>Editar Perfil</DialogTitle>
          <DialogDescription>
            Atualize suas informações pessoais e de segurança.
          </DialogDescription>
        </DialogHeader>

        {/* Mensagens de erro/sucesso */}
        {error && <p className="text-red-600 text-sm mb-4">{error}</p>}
        {successMessage && <p className="text-green-600 text-sm mb-4">{successMessage}</p>}

        {/* Seção de Informações Pessoais */}
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 gap-4">
            <div className="col-span-1">
              <div className="relative group flex justify-center items-center h-full">
                <Avatar className="h-20 w-20 cursor-pointer">
                  <AvatarImage
                    src={avatarPreview || undefined}
                    alt={user.nome}
                    className="object-cover w-full h-full"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      if (user?.avatarUrl) {
                        // Try with the full URL if it's a relative path
                        const fullUrl = user.avatarUrl.startsWith('http')
                          ? user.avatarUrl
                          : `${API_BASE_URL}${user.avatarUrl.startsWith('/') ? '' : '/'}${user.avatarUrl}`;

                        if (target.src !== fullUrl) {
                          target.src = fullUrl;
                        } else {
                          target.style.display = 'none';
                        }
                      } else {
                        target.style.display = 'none';
                      }
                    }}
                  />
                  <AvatarFallback className="bg-primary text-white w-full h-full flex items-center justify-center">
                    {getInitials(user.nome)}
                  </AvatarFallback>
                </Avatar>
                <label
                  htmlFor="avatar-upload"
                  className={`absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-full cursor-pointer transition-opacity duration-200 ${
                    (avatarPreview || user?.avatarUrl) ? 'opacity-0 group-hover:opacity-100' : 'opacity-30 group-hover:opacity-100'
                  }`}
                >
                  <Camera className="h-6 w-6 text-white" />
                </label>
              </div>
              <input
                id="avatar-upload"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleAvatarChange}
              />
            </div>
            <div className="col-span-3 grid gap-4"> {/* Adicionado gap-4 para espaçamento consistente */}
              {/* INPUT: Nome - CORRIGIDO/VERIFICADO */}
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="nome" className="text-right">
                  Nome
                </Label>
                <Input
                  id="nome"
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  placeholder="Digite seu nome"
                  className="col-span-3"
                />
              </div>
              {/* INPUT: Email - DESABILITADO (OK) */}
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="email" className="text-right">
                  Email
                </Label>
                <Input id="email" value={user.email} disabled className="col-span-3" />
              </div>
              {/* INPUT: Perfil - DESABILITADO (OK) */}
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="perfil" className="text-right">
                  Perfil
                </Label>
                <Input id="perfil" value={user.role} disabled className="col-span-3" />
              </div>
            </div>
          </div>
        </div>
        <DialogFooter className="sm:justify-between">
          <DialogClose asChild>
            <Button type="button" variant="secondary">
              Cancelar
            </Button>
          </DialogClose>
          <Button type="button" onClick={handleSaveProfile} disabled={isSavingProfile}>
            {isSavingProfile ? 'Salvando...' : 'Salvar Alterações'}
          </Button>
        </DialogFooter>

        {/* Seção de Alteração de Senha */}
        <div className="border-t pt-4 mt-4">
          <h3 className="text-lg font-medium mb-2">Alterar Senha</h3>
          <div className="grid gap-4">
            {/* INPUT: Senha Atual - CORRIGIDO/VERIFICADO */}
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="senhaAtual" className="text-right">
                Senha Atual
              </Label>
              <Input
                id="senhaAtual"
                type="password"
                value={senhaAtual}
                onChange={(e) => setSenhaAtual(e.target.value)}
                className="col-span-3"
              />
            </div>
            {/* INPUT: Nova Senha - CORRIGIDO/VERIFICADO */}
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="novaSenha" className="text-right">
                Nova Senha
              </Label>
              <Input
                id="novaSenha"
                type="password"
                value={novaSenha}
                onChange={(e) => setNovaSenha(e.target.value)}
                className="col-span-3"
              />
            </div>
            {/* INPUT: Confirmar Nova Senha - CORRIGIDO/VERIFICADO */}
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="confirmarNovaSenha" className="text-right">
                Confirmar Nova Senha
              </Label>
              <Input
                id="confirmarNovaSenha"
                type="password"
                value={confirmarNovaSenha}
                onChange={(e) => setConfirmarNovaSenha(e.target.value)}
                className="col-span-3"
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button type="button" onClick={handleChangePassword} disabled={isChangingPassword}>
              {isChangingPassword ? 'Alterando...' : 'Alterar Senha'}
            </Button>
          </DialogFooter>
        </div>

      </DialogContent>
    </Dialog>
  );
}